import { type SearchResponse } from "@elastic/elasticsearch/lib/api/types";
import { type FIELDS } from "@utils/constants";
import { type ElasticSearchPostResult } from "@utils/elasticSearchUtils";
import { getQueryParamAsArray } from "@utils/utils";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { useQuery } from "react-query";
import { type ArticleReturnType } from "~/lib/db/types";

const useHighlightedPost = (
  originalPost: ArticleReturnType,
  restrictAccess: boolean
): [post: ArticleReturnType, loading: boolean, error: Error | undefined] => {
  const router = useRouter();
  const searchText = router.query.q;
  const fields = getQueryParamAsArray<FIELDS>(router.query.fields || []);

  const { isLoading, error, data } = useQuery<
    ElasticSearchPostResult["hits"]["hits"][0]["highlight"],
    Error
  >({
    queryKey: [
      "highligh-post",
      searchText,
      router.query.fields,
      router.query.searchType,
    ],
    queryFn: () => {
      if (!searchText) return;

      const urlParams = new URLSearchParams();

      urlParams.set("q", searchText as string);
      urlParams.set("postId", originalPost.post_id);

      fields.forEach((field) => urlParams.append("fields", field));

      if (router.query.searchType) {
        urlParams.set("searchType", router.query.searchType as string);
      }

      return fetch(`/api/highlight?${urlParams.toString()}`)
        .then((res) => res.json())
        .then(
          (elasticResult: SearchResponse) =>
            elasticResult?.hits?.hits?.[0]?.highlight
        );
    },
    enabled: !restrictAccess,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (
      router.isReady &&
      data &&
      !isLoading &&
      router.query.q &&
      !router.asPath.includes("#")
    )
      setTimeout(() => document.querySelector("mark")?.scrollIntoView(), 500);
  }, [data, isLoading]);

  if (!originalPost) return [undefined, isLoading, error];

  return [
    {
      ...originalPost,
      post_title:
        data?.post_title?.[0] ||
        data?.["post_title.exact"]?.[0] ||
        originalPost?.post_title,
      post_content:
        data?.post_content?.[0] ||
        data?.["post_content.exact"]?.[0] ||
        originalPost?.post_content,
    },
    isLoading,
    error,
  ];
};

export default useHighlightedPost;
